import { applySnapshot, flow, Instance, types } from 'mobx-state-tree';
import { Tabs } from '../../http-requests';
import { RequestStateModel } from './general-models';
import { IParams } from '../../http-requests/types';

const LinkModel = types.model({
  id: types.optional(types.string, ''),
  help_link: types.optional(types.string, ''),
  resource_name: types.optional(types.string, ''),
});

export interface ILinkModel extends Instance<typeof LinkModel> {}

const TabModel = types
  .model({
    description: types.optional(types.string, ''),
    help: types.optional(types.string, ''),
    id: types.optional(types.identifier, ''),
    parent: types.optional(types.string, ''),
    slug: types.optional(types.string, ''),
    title: types.optional(types.string, ''),
    title_ext: types.optional(types.string, ''),
  })
  .views((self) => ({
    //...
  }))
  .actions((self) => ({
    setHelp(value: string) {
      self.help = value;
    },
    setDescription(value: string) {
      self.description = value;
    },
  }));

export interface ITabModel extends Instance<typeof TabModel> {}

const RequestsStates = types.model({
  getTabsList: types.optional(RequestStateModel, {}),
  getTab: types.optional(RequestStateModel, {}),
  editTab: types.optional(RequestStateModel, {}),
  linkTab: types.optional(RequestStateModel, {}),
  editLinkTab: types.optional(RequestStateModel, {}),
});

export const TabsStore = types
  .model({
    requestsStates: types.optional(RequestsStates, {}),
    tabs: types.optional(types.array(TabModel), []),
    currentTab: types.optional(types.maybeNull(TabModel), {}),
    currentTabSnapshot: types.optional(types.maybeNull(TabModel), {}),
    tabLinks: types.optional(types.array(LinkModel), []),
  })
  .views((self) => ({
    get currentHasChanges(): boolean {
      return self.currentTab!.help !== self.currentTabSnapshot!.help;
    },
    currentLinks(slug: string): ILinkModel {
      return self.tabLinks.find(
        (tabLink) => tabLink.resource_name === slug
      ) as ILinkModel;
    },
  }))
  .actions((self) => ({
    hardResetCurrentTab() {
      self.currentTab = null;
    },
    resetCurrentTab() {
      if (self.currentTab !== null) {
        applySnapshot(self.currentTab, self.currentTabSnapshot);
      }
    },
    setCurrentTab(tab: ITabModel) {
      self.currentTab = tab;
      self.currentTabSnapshot = tab;
    },
    getTabsList: flow(function* () {
      try {
        self.requestsStates.getTabsList.loadingStates();

        const response: any = yield Tabs.GetTabsList();
        self.tabs = response.results;
        self.requestsStates.getTabsList.successStates();

        return response;
      } catch (error) {
        self.requestsStates.getTabsList.errorStates();
        console.error(error);
      } finally {
        self.requestsStates.getTabsList.finallyStates();
      }
    }),
    getTab: flow(function* (id: string) {
      try {
        if (self.currentTab?.id === id) {
          return;
        }

        self.requestsStates.getTab.loadingStates();

        const response = yield Tabs.GetTab(id);
        self.currentTab = response;
        self.currentTabSnapshot = response;

        self.requestsStates.getTab.successStates();

        return response;
      } catch (error) {
        self.requestsStates.getTab.errorStates();
        console.error(error);
      } finally {
        self.requestsStates.getTab.finallyStates();
      }
    }),
    editTab: flow(function* (params: IParams) {
      try {
        if (!self.currentTab?.id) {
          return;
        }

        self.requestsStates.editTab.loadingStates();

        const response = yield Tabs.EditTab({
          ...params,
          tab: self.currentTab.id,
        });
        self.currentTab = response;
        self.currentTabSnapshot = response;

        self.requestsStates.editTab.successStates();

        return response;
      } catch (error) {
        self.requestsStates.editTab.errorStates();
        console.error(error);
      } finally {
        self.requestsStates.editTab.finallyStates();
      }
    }),
    getTabLinks: flow(function* () {
      try {
        self.requestsStates.linkTab.loadingStates();

        const response = yield Tabs.GetTabLinks();

        self.tabLinks = response.results;
        self.requestsStates.linkTab.successStates();

        return response;
      } catch (error) {
        console.error(error);
        self.requestsStates.linkTab.errorStates();
      } finally {
        self.requestsStates.linkTab.finallyStates();
      }
    }),
    sendNewLink: flow(function* (id: string, data: IParams) {
      try {
        self.requestsStates.editLinkTab.loadingStates();
        const response = yield Tabs.PatchTabLink(id, data);
        self.requestsStates.editLinkTab.successStates();

        return response;
      } catch (error) {
        console.error(error);
        self.requestsStates.editLinkTab.errorStates();

        return Promise.reject(error);
      } finally {
        self.requestsStates.editLinkTab.finallyStates();
      }
    }),
  }));
